import { Gestures } from 'rxjs-gestures';
import { 
    curPoints as curPointsSubject,
    forwardsEdgeStartEndIdxs as forwardsEdgeStartEndIdxsSubject,
    forwardsEdgeToUnsortedEdge as forwardsEdgeToUnsortedEdgeSubject,
    logicalEdges as logicalEdgesSubject,
} from '@graphistry/viz/src/client/legacy';
import { createSubject } from './createSubject';
import { ReplaySubject } from '@reactivex/rxjs';
import { pointIndexesInRect } from './pointIndexesInRect';
import { getInteriorEdges } from '@graphistry/viz/src/utils/wavefrontExpand';

function getReplaySubject1() {
    return new ReplaySubject(1);
}
function getIdentifier({ identifier = 'mouse' }) {
    return identifier;
}

export class SceneGestures extends Gestures {
    static startsByIdFromActions(actions) {
        return this.startFromActions(actions).groupBy(getIdentifier, null, null, getReplaySubject1);
    }
    static wheelFromActions(actions) {
        return SceneGestures.to(SceneGestures.wheel.bind(SceneGestures), actions);
    }
    static startFromActions(actions) {
        return SceneGestures.to(SceneGestures.start.bind(SceneGestures), actions);
    }
    static moveFromActions(actions) {
        return SceneGestures.to(SceneGestures.move.bind(SceneGestures), actions);
    }
    static endFromActions(actions) {
        return SceneGestures.to(SceneGestures.end.bind(SceneGestures), actions);
    }
    static cancelFromActions(actions) {
        return SceneGestures.to(SceneGestures.cancel.bind(SceneGestures), actions);
    }
    static tapFromActions(actions) {
        return SceneGestures.to(SceneGestures.tap.bind(SceneGestures), actions);
    }
    static pressFromActions(actions) {
        return SceneGestures.to(SceneGestures.press.bind(SceneGestures), actions);
    }
    static panFromActions(actions) {
        return SceneGestures.to(SceneGestures.pan.bind(SceneGestures), actions);
    }
    static to(gesture, actions) {
        return new this(
            actions.multicast(createSubject, actions =>
                gesture(actions.pluck('event'))
                    .normalize()
                    .withActions(actions)
            )
        );
    }
    lift(operator) {
        const observable = new SceneGestures(this);
        observable.operator = operator;
        return observable;
    }
    withActions(actions) {
        return this.zip(actions, (point, action) => {
            for (const key in action) {
                if (key !== 'event') {
                    point[key] = action[key];
                }
            }
            point.buttons = action.event.buttons;
            return point;
        });
    }
    withModifiedSelection() {
        return this.withLatestFrom(
            curPointsSubject, forwardsEdgeStartEndIdxsSubject, forwardsEdgeToUnsortedEdgeSubject, logicalEdgesSubject,
            (gesture, curPoints, forwardsEdgeStartEndIdxs, forwardsEdgeToUnsortedEdge, logicalEdges) => {
                const { rect, selection, event: {altKey, shiftKey} } = gesture;
                const { point: points, edge: edges } = selection || {point: [], edge: []};

                const newPoints = rect ? pointIndexesInRect(curPoints, rect.tl, rect.br) : [];

                var resultingPoints = [];

                if (!shiftKey && !altKey) {
                    resultingPoints = newPoints;
                } else if (shiftKey && !altKey) {
                    resultingPoints = [...new Set([...points, ...newPoints])];
                } else if (!shiftKey && altKey) {
                    const newPointsSet = new Set(newPoints);
                    resultingPoints = points.filter(p => !newPointsSet.has(p));
                } else if (shiftKey && altKey) {
                    const newPointsSet = new Set(newPoints);
                    resultingPoints = points.filter(p => newPointsSet.has(p));
                }

                gesture.modifiedSelection = {
                    points: resultingPoints,
                    edges: getInteriorEdges(resultingPoints, forwardsEdgeStartEndIdxs, forwardsEdgeToUnsortedEdge, logicalEdges)
                }

                return gesture;
            });
    }
    mapToWorldCoords() {
        return this.map(point => {
            const {
                xOrigin,
                yOrigin,
                movementXTotal,
                movementYTotal,
                renderState: { camera, canvas }
            } = point;

            const { x: worldX, y: worldY } = camera.canvas2WorldCoords(
                xOrigin + movementXTotal,
                yOrigin + movementYTotal,
                canvas
            );

            const { x: worldXOrigin, y: worldYOrigin } = camera.canvas2WorldCoords(
                xOrigin,
                yOrigin,
                canvas
            );

            point.worldX = worldX;
            point.worldY = worldY;
            point.worldXOrigin = worldXOrigin;
            point.worldYOrigin = worldYOrigin;

            return point;
        });
    }
    dragRectInWorldCoords() {
        return this.map(point => {
            const {
                xOrigin,
                yOrigin,
                movementXTotal,
                movementYTotal,
                renderState: { camera, canvas }
            } = point;

            const rect = point.rect || (point.rect = {});

            rect.tl = camera.canvas2WorldCoords(
                Math.min(xOrigin, xOrigin + movementXTotal),
                Math.min(yOrigin, yOrigin + movementYTotal),
                canvas
            );

            rect.br = camera.canvas2WorldCoords(
                Math.max(xOrigin, xOrigin + movementXTotal),
                Math.max(yOrigin, yOrigin + movementYTotal),
                canvas
            );

            return point;
        });
    }
    moveRectInWorldCoords() {
        return this.map(point => {
            const { rect } = point;
            if (rect) {
                const { camera, movementX = 0, movementY = 0 } = point;
                const { width = 1, height = 1, simulationWidth = 1, simulationHeight = 1 } = camera;
                const { tl, br } = rect;
                tl.x += movementX * width / simulationWidth;
                tl.y -= movementY * height / simulationHeight;
                br.x += movementX * width / simulationWidth;
                br.y -= movementY * height / simulationHeight;
            }
            return point;
        });
    }
    moveCameraInWorldCoords() {
        return this.map(point => {
            const { camera, isKeplerOpen } = point;
            if (camera) {
                const { movementX = 0, movementY = 0 } = point;
                const {
                    center,
                    width = 1,
                    height = 1,
                    simulationWidth = 1,
                    simulationHeight = 1
                } = camera;
                center.x -= movementX * width / simulationWidth;
                center.y -= movementY * height / simulationHeight;

                if (isKeplerOpen) {
                    SceneGestures.clampKeplerCamera(camera);
                }
            }
            return point;
        });
    }

    // Modified Earth circumference (scaled down by 10000x for manageable numbers)
    static EARTH_CIRCUMFERENCE = 4007.5016686; // ~4,007 instead of 40,075,016
    static MAX_MERCATOR = SceneGestures.EARTH_CIRCUMFERENCE / 2; // ~2,003 instead of 20,037,508

    /**
     * Clamps camera position to Mercator projection bounds
     * @param {Object} camera - Camera object with center, width, and height
     */
    static clampKeplerCamera(camera) {
        const { MAX_MERCATOR } = SceneGestures;
        const { center, height } = camera;
        const halfHeight = height / 2;

        // Clamp vertical position
        const topEdge = center.y + halfHeight;
        const bottomEdge = center.y - halfHeight;
        if (topEdge > MAX_MERCATOR) {
            center.y = MAX_MERCATOR - halfHeight;
        } else if (bottomEdge < -MAX_MERCATOR) {
            center.y = -MAX_MERCATOR + halfHeight;
        }

        // Clamp horizontal position
        if (center.x > MAX_MERCATOR) {
            center.x = MAX_MERCATOR;
        } else if (center.x < -MAX_MERCATOR) {
            center.x = -MAX_MERCATOR;
        }
    }
}
